import * as React from 'react'
import dayjs from 'dayjs'
import LocalizedFormatPlugin from 'dayjs/plugin/localizedFormat'
import AdvancedFormatPlugin from 'dayjs/plugin/advancedFormat'
import TimezonePlugin from 'dayjs/plugin/timezone'

dayjs.extend(LocalizedFormatPlugin)
dayjs.extend(AdvancedFormatPlugin)
dayjs.extend(TimezonePlugin)

export interface Interpolations {
  [key: string]: React.ReactNode
}

interface Macro {
  value: string
  script: string
  type?: string
}

interface Macros {
  [key: string]: Macro
}

const macrosRegExp = /({{(a|i):(.+?)}})/gi

// Extracts the values of a nested object
function getObjectValues(obj: object): string[] {
  return ((obj && typeof obj === 'object')
    ? Object.values(obj).map(getObjectValues).flat()
    : [obj]) as string[]
}

export function getMacros(obj: object): string[] {
  return getObjectValues(obj)
    .map(value => `${value}`.match(macrosRegExp) || [])
    .reduce((acc, val) => acc.concat(val), [])
    .filter((value, index, self) => self.indexOf(value) === index)
}

function formatDate(isoDate: string) {
  return dayjs(isoDate).format('l (hh:mm a z)')
}

function processMacroValue(macro: Macro) {
  switch (macro.type) {
    case 'date':
      return formatDate(macro.value)

    default:
      return macro.value
  }
}

export function interpolateMacros(copy: object | any[] | number | string, macros: Macros): object | any[] | number | string {
  function replacer(match: string) {
    if (!macros[match]) {
      return null
    }

    return processMacroValue(macros[match])
  }

  if (typeof copy === 'number') {
    return copy
  }

  if (typeof copy === 'string') {
    return copy.replace(macrosRegExp, replacer)
  }

  if (copy.constructor === Array) {
    return copy.map(item => interpolateMacros(item, macros))
  }

  return Object.keys(copy).reduce((a, c) => ({ ...a, [c]: interpolateMacros(copy[c], macros) }), {})
}

export const interpolateContent = (str: string, interpolations?: Interpolations) => {
  const parts = str.split(/({{[^{}]*}})/)

  return parts.map((part: string, index: number) => {
    const match = part.match(/{{([^{}]*)}}/)
    return match && interpolations
      ? <React.Fragment key={`${part}-${index}`}>{interpolations[match[1]]}</React.Fragment>
      : <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
  })
}

// Bespoke interstitials ids which should use default copy
export const USE_DEFAULT_LANGUAGE_BESPOKE_IDS = ['logo']

export const getLocale = (params: { language: string; country: string; bespokeId?: string }) => {
  const language = params.language.toLowerCase()
  const bespokeId = params.bespokeId?.toLowerCase()
  const { country } = params

  if (bespokeId && USE_DEFAULT_LANGUAGE_BESPOKE_IDS.indexOf(bespokeId) === -1) {
    return `${bespokeId}/${language}_${country}`
  }

  return `${language}_${country}`
}

export const getCopyParams = (params: {
  matchZeroInterest?: boolean
  isThreshold: boolean
  paymentMethodId?: number
  adPaymentMethods?: number[]
  bespokeId?: string
}) => {
  let nonAmountBasedPath = ''
  let copyParams = []

  // If bespokeId is external_content, modal should display generic copy
  if (params.bespokeId === 'external_content') {
    copyParams = ['generic']
    nonAmountBasedPath = 'generic'

    return { copyParams, nonAmountBasedPath }
  }

  // If paymentMethodId is passed, it's the amount based ad
  if (params.paymentMethodId) {
    if (params.isThreshold) {
      copyParams.push(`amount_based.threshold.${params.paymentMethodId}`)
    } else {
      if (params.matchZeroInterest) {
        copyParams.push(`amount_based.zero_interest.${params.paymentMethodId}`)
      }
      copyParams.push(`amount_based.${params.paymentMethodId}`)
    }
    nonAmountBasedPath = `non_amount_based.${params.paymentMethodId}`
    // If paymentMethodId is not passed, it's the non-amount based ad
    // Check if it has only one tier and get payment method from it
  } else if (params.adPaymentMethods && params.adPaymentMethods.length === 1) {
    nonAmountBasedPath = `non_amount_based.${params.adPaymentMethods[0]}`
  }

  if (nonAmountBasedPath) {
    copyParams.push(nonAmountBasedPath)
  }

  // If there's no paymentMethodId and more than one paymentMethodId in adPaymentMethods
  // show generic copy
  if (!copyParams.length) {
    copyParams = ['generic']
    nonAmountBasedPath = 'generic'
  }

  return { copyParams, nonAmountBasedPath }
}
